import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, User, ArrowRight, Check, Eye, EyeOff, Loader2, LucideIcon } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { cn } from '@/lib/cn';

// Custom DryVox Logo
const DryVoxLogo = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => {
  const sizeClasses = { sm: 'w-5 h-5', md: 'w-6 h-6', lg: 'w-7 h-7' };
  return (
    <svg viewBox="0 0 32 32" fill="none" className={sizeClasses[size]}>
      <circle cx="16" cy="6" r="4" stroke="currentColor" strokeWidth="2.5" fill="none"/>
      <path d="M16 10V26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M10 16H22" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M8 28C8 22 12 20 16 26C20 20 24 22 24 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      <path d="M4 14C5.5 12 5.5 10 4 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
      <path d="M28 14C26.5 12 26.5 10 28 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.6"/>
    </svg>
  );
};

const passwordRequirements = [
  { regex: /.{8,}/, label: 'At least 8 characters' },
  { regex: /[A-Z]/, label: 'One uppercase letter' },
  { regex: /[a-z]/, label: 'One lowercase letter' },
  { regex: /[0-9]/, label: 'One number' },
];

// InputField component - MOVED OUTSIDE to prevent re-creation on every render
interface InputFieldProps {
  id: string;
  type?: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  isFocused: boolean;
  icon: LucideIcon;
  showPassword?: boolean;
  onTogglePassword?: () => void;
  showToggle?: boolean;
  error?: string;
  autoComplete?: string;
}

const InputField = ({ 
  id, 
  type = 'text', 
  label, 
  value, 
  onChange,
  onFocus,
  onBlur,
  isFocused,
  icon: Icon,
  showPassword,
  onTogglePassword,
  showToggle = false,
  error,
  autoComplete,
}: InputFieldProps) => (
  <div className="relative">
    <label 
      htmlFor={id}
      className={cn(
        "absolute left-12 transition-all duration-200 pointer-events-none z-10",
        isFocused || value
          ? "-top-2.5 text-xs px-2 bg-slate-900 rounded text-teal-400 font-medium"
          : "top-1/2 -translate-y-1/2 text-slate-400"
      )}
    >
      {label}
    </label>
    <div className="relative">
      <div className={cn(
        "absolute left-4 top-1/2 -translate-y-1/2 transition-colors",
        isFocused ? "text-teal-400" : "text-slate-500"
      )}>
        <Icon className="w-5 h-5" />
      </div>
      <input
        id={id}
        type={showToggle ? (showPassword ? 'text' : 'password') : type}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        className={cn(
          "w-full h-14 pl-12 rounded-xl",
          "bg-slate-800/50 border-2",
          error ? "border-red-500/50" : "border-slate-700/50",
          "text-white font-medium placeholder-transparent",
          "transition-all duration-200",
          "hover:border-slate-600",
          "focus:outline-none focus:border-teal-500/50 focus:bg-slate-800/80",
          "focus:ring-4 focus:ring-teal-500/10",
          showToggle ? "pr-12" : "pr-4"
        )}
        autoComplete={autoComplete}
      />
      {showToggle && onTogglePassword && (
        <button
          type="button"
          onClick={onTogglePassword}
          className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300 transition-colors"
        >
          {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
    </div>
    {error && (
      <p className="text-red-400 text-xs mt-1.5 ml-1">{error}</p>
    )}
  </div>
);

/**
 * Register Page - Modern Fintech Design
 */
export function Register() {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuthStore();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);

  const checkRequirement = (regex: RegExp) => regex.test(password);
  const allRequirementsMet = passwordRequirements.every((req) => checkRequirement(req.regex));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');
    clearError();

    if (!name.trim()) {
      setFormError('Full name is required');
      return;
    }
    if (!email.trim()) {
      setFormError('Email is required');
      return;
    }
    if (!password) {
      setFormError('Password is required');
      return;
    }
    if (!allRequirementsMet) {
      setFormError('Password does not meet all requirements');
      return;
    }
    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    try {
      await register(email, password, name);
      navigate('/dashboard', { replace: true });
    } catch (err: any) {
      setFormError(err.message || 'Registration failed. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] flex items-center justify-center p-4 py-8 relative overflow-hidden">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-[#030712] to-[#030712]" />
        
        <div 
          className="absolute inset-0 opacity-40"
          style={{
            background: `
              radial-gradient(ellipse 80% 50% at 20% 40%, rgba(56, 189, 248, 0.15) 0%, transparent 50%),
              radial-gradient(ellipse 60% 40% at 70% 60%, rgba(34, 211, 238, 0.1) 0%, transparent 50%),
              radial-gradient(ellipse 50% 30% at 50% 80%, rgba(20, 184, 166, 0.12) 0%, transparent 50%)
            `
          }}
        />

        <motion.div
          animate={{ y: [0, -30, 0], x: [0, 15, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(6, 182, 212, 0.08) 0%, transparent 70%)' }}
        />
        <motion.div
          animate={{ y: [0, 20, 0], x: [0, -20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full blur-3xl"
          style={{ background: 'radial-gradient(circle, rgba(20, 184, 166, 0.06) 0%, transparent 70%)' }}
        />

        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px'
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center mb-8"
        >
          <Link to="/" className="inline-flex flex-col items-center gap-4 group">
            <motion.div 
              whileHover={{ rotate: [0, -10, 10, 0] }}
              transition={{ duration: 0.5 }}
              className="relative"
            >
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center shadow-xl shadow-cyan-500/25 text-white">
                <DryVoxLogo size="lg" />
              </div>
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-teal-400 to-cyan-500 blur-xl opacity-40 group-hover:opacity-60 transition-opacity" />
            </motion.div>
            <div>
              <h1 className="text-2xl font-display font-bold tracking-tight">
                <span className="text-white">Dry</span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-400">Vox</span>
              </h1>
              <p className="text-xs text-slate-500 mt-1 font-medium">Jarvis for Shipbrokers</p>
            </div>
          </Link>
        </motion.div>

        {/* Register Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={cn(
            "p-8 rounded-3xl",
            "bg-gradient-to-b from-slate-800/60 to-slate-900/60",
            "backdrop-blur-xl border border-white/10",
            "shadow-2xl shadow-black/40"
          )}
        >
          <div className="text-center mb-6">
            <h2 className="text-2xl font-display font-bold text-white">Create account</h2>
            <p className="text-slate-400 mt-2 text-sm">Start your free trial today</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <InputField
              id="name"
              label="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onFocus={() => setFocusedField('name')}
              onBlur={() => setFocusedField(null)}
              isFocused={focusedField === 'name'}
              icon={User}
              autoComplete="name"
            />

            <InputField
              id="email"
              type="email"
              label="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setFocusedField('email')}
              onBlur={() => setFocusedField(null)}
              isFocused={focusedField === 'email'}
              icon={Mail}
              autoComplete="email"
            />

            <InputField
              id="password"
              type="password"
              label="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setFocusedField('password')}
              onBlur={() => setFocusedField(null)}
              isFocused={focusedField === 'password'}
              icon={Lock}
              showToggle
              showPassword={showPassword}
              onTogglePassword={() => setShowPassword(!showPassword)}
              autoComplete="new-password"
            />

            {/* Password Requirements */}
            {password && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="grid grid-cols-2 gap-2 pt-1"
              >
                {passwordRequirements.map((req, index) => {
                  const isMet = checkRequirement(req.regex);
                  return (
                    <div
                      key={index}
                      className={cn(
                        'flex items-center gap-2 text-xs transition-colors',
                        isMet ? 'text-emerald-400' : 'text-slate-500'
                      )}
                    >
                      <div
                        className={cn(
                          'w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0',
                          isMet ? 'bg-emerald-500/20' : 'bg-slate-700/50'
                        )}
                      >
                        {isMet && <Check className="w-2.5 h-2.5" />}
                      </div>
                      <span>{req.label}</span>
                    </div>
                  );
                })}
              </motion.div>
            )}

            <InputField
              id="confirmPassword"
              type="password"
              label="Confirm password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField(null)}
              isFocused={focusedField === 'confirmPassword'}
              icon={Lock}
              showToggle
              showPassword={showConfirmPassword}
              onTogglePassword={() => setShowConfirmPassword(!showConfirmPassword)}
              error={confirmPassword && password !== confirmPassword ? 'Passwords do not match' : undefined}
              autoComplete="new-password"
            />

            {/* Error Message */}
            {(error || formError) && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 rounded-xl bg-red-500/10 border border-red-500/20"
              >
                <p className="text-sm text-red-400 text-center font-medium">
                  {error || formError}
                </p>
              </motion.div>
            )}

            {/* Submit Button */}
            <motion.button
              type="submit"
              disabled={isLoading || (!allRequirementsMet && password.length > 0)}
              whileHover={{ scale: isLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading ? 1 : 0.99 }}
              className={cn(
                "w-full h-14 rounded-xl font-semibold text-base",
                "bg-gradient-to-r from-teal-500 to-cyan-500",
                "hover:from-teal-400 hover:to-cyan-400",
                "shadow-lg shadow-cyan-500/25 hover:shadow-cyan-500/40",
                "transition-all duration-300",
                "flex items-center justify-center gap-3",
                "disabled:opacity-70 disabled:cursor-not-allowed"
              )}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Creating account...
                </>
              ) : (
                <>
                  Create account
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </motion.button>
          </form>

          {/* Sign in link */}
          <div className="mt-6 pt-6 border-t border-slate-700/50 text-center">
            <p className="text-slate-400 text-sm">
              Already have an account?{' '}
              <Link
                to="/login"
                className="text-teal-400 hover:text-teal-300 font-semibold transition-colors inline-flex items-center gap-1 group"
              >
                Sign in
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center text-slate-600 text-sm mt-8"
        >
          By creating an account, you agree to our{' '}
          <a href="#" className="text-slate-500 hover:text-teal-400 transition-colors">Terms</a>
          {' & '}
          <a href="#" className="text-slate-500 hover:text-teal-400 transition-colors">Privacy</a>
        </motion.p>
      </div>
    </div>
  );
}

export default Register;
